// src/layout/components/finance/PerfMiniLines.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const BR_TZ = "America/Sao_Paulo";

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function fmtCompact(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  const a = Math.abs(x);

  if (a >= 1_000_000) return `${(x / 1_000_000).toFixed(Math.abs(x / 1_000_000) >= 10 ? 0 : 1)}M`;
  if (a >= 1_000) return `${(x / 1_000).toFixed(Math.abs(x / 1_000) >= 10 ? 0 : 1)}k`;
  if (Number.isInteger(x)) return String(x);
  return x.toFixed(1);
}

function tickFmtDate(ms) {
  const t = Number(ms);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return d.toLocaleDateString("pt-BR", { timeZone: BR_TZ, day: "2-digit", month: "2-digit" });
}

function labelFmtDate(ms) {
  const t = Number(ms);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return d.toLocaleDateString("pt-BR", {
    timeZone: BR_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function tooltipFormatter(value, name) {
  if (value == null) return ["—", name];
  const n = Number(value);
  if (!Number.isFinite(n)) return ["—", name];
  return [fmtCompact(n), name];
}

function startOfTodayLocalMs(nowMs) {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function startOfTomorrowLocalMs(nowMs) {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d.getTime();
}

// mede o tamanho real do container (pra posicionar a linha em px)
function useBoxSize(ref) {
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const r = entries?.[0]?.contentRect;
      if (!r) return;
      setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return box;
}

/**
 * PerfMiniLines (com “linhas verticais” desenhadas por overlay na ÁREA DO PLOT)
 *
 * props:
 * - items: array
 * - xKey: ms epoch
 * - series: [{ key, name, stroke, dashed, width, connectNulls, cutAfterToday }]
 * - windowStartMs/windowEndMs: fixa eixo X
 * - showTodayLine/showNowLine
 * - nowTick: re-render a cada ~30s
 */
export default function PerfMiniLines({
  items = [],
  theme,
  height = 240,

  xKey = "bucket_start_utc_ms",
  series = [],

  windowStartMs = null,
  windowEndMs = null,

  showTodayLine = true,
  showNowLine = false,

  nowTick = null,
}) {
  const t = theme || {};
  const nowMs = Number(nowTick ?? Date.now());

  const ws = safeNum(windowStartMs);
  const we = safeNum(windowEndMs);

  const todayX = useMemo(() => startOfTodayLocalMs(nowMs), [nowMs]);
  const cutFromMs = useMemo(() => startOfTomorrowLocalMs(nowMs), [nowMs]);

  const grid = "rgba(255,255,255,0.08)";
  const axis = "rgba(255,255,255,0.16)";
  const tick = "rgba(255,255,255,0.65)";

  // mesmos margins que você já usa
  const MARGIN = useMemo(() => ({ top: 8, right: 8, left: 6, bottom: 4 }), []);
  const Y_AXIS_W = 46;      // igual ao seu YAxis
  const X_AXIS_H = 26;      // aproximação boa (padrão recharts)
  const LABEL_TOP_PAD = 8;  // label dentro do plot

  const wrapRef = useRef(null);
  const { w: boxW, h: boxH } = useBoxSize(wrapRef);

  const baseData = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    return arr
      .map((r) => {
        const x = safeNum(r?.[xKey]);
        if (x == null) return null;
        return { ...r, __x: x };
      })
      .filter(Boolean)
      .sort((a, b) => a.__x - b.__x);
  }, [items, xKey]);

  const xDomain = useMemo(() => {
    if (ws != null && we != null && we > ws) return [ws, we];
    return ["dataMin", "dataMax"];
  }, [ws, we]);

  const data = useMemo(() => {
    return baseData.map((r) => {
      const row = { ...r };
      for (const s of series) {
        const raw = safeNum(r?.[s.key]);
        row[`__${s.key}`] = s?.cutAfterToday && r.__x >= cutFromMs ? null : raw;
      }
      return row;
    });
  }, [baseData, series, cutFromMs]);

  // range efetivo (janela fixa manda)
  const xMin = useMemo(() => {
    if (ws != null && we != null && we > ws) return ws;
    return data.length ? data[0].__x : null;
  }, [ws, we, data]);

  const xMax = useMemo(() => {
    if (ws != null && we != null && we > ws) return we;
    return data.length ? data[data.length - 1].__x : null;
  }, [ws, we, data]);

  // só desenha se cair dentro (padrão do ingest)
  const showNowLineOk =
    showNowLine && xMin != null && xMax != null && nowMs >= xMin && nowMs <= xMax;

  const showTodayLineOk =
    showTodayLine && xMin != null && xMax != null && todayX >= xMin && todayX <= xMax;

  // converte X -> posição em px DENTRO do plot
  const plotBox = useMemo(() => {
    const left = MARGIN.left + Y_AXIS_W;
    const right = MARGIN.right;
    const top = MARGIN.top;
    const bottom = MARGIN.bottom + X_AXIS_H;
    const plotW = Math.max(0, boxW - left - right);
    const plotH = Math.max(0, boxH - top - bottom);
    return { left, top, plotW, plotH };
  }, [boxW, boxH, MARGIN, Y_AXIS_W]);

  function xToPlotPx(xVal) {
    if (xMin == null || xMax == null) return null;
    if (!Number.isFinite(xVal) || !Number.isFinite(xMin) || !Number.isFinite(xMax) || xMax <= xMin) return null;
    const p = (xVal - xMin) / (xMax - xMin);
    if (!Number.isFinite(p)) return null;
    const clamped = Math.max(0, Math.min(1, p));
    return plotBox.left + clamped * plotBox.plotW;
  }

  const nowPx = useMemo(() => (showNowLineOk ? xToPlotPx(nowMs) : null), [showNowLineOk, nowMs, xMin, xMax, plotBox]);
  const todayPx = useMemo(() => (showTodayLineOk ? xToPlotPx(todayX) : null), [showTodayLineOk, todayX, xMin, xMax, plotBox]);

  return (
    <div ref={wrapRef} style={{ width: "100%", height, position: "relative" }}>
      {/* ✅ Overlay na ÁREA DO PLOT (fica “igual ReferenceLine”) */}
      {todayPx != null && (
        <>
          <div
            style={{
              position: "absolute",
              left: todayPx,
              top: plotBox.top,
              height: plotBox.plotH,
              borderLeft: "2px dashed rgba(255,255,255,0.35)",
              pointerEvents: "none",
              zIndex: 6,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: todayPx + 6,
              top: plotBox.top + LABEL_TOP_PAD,
              color: "rgba(255, 255, 255, 0.86)",
              fontSize: 12,
              fontWeight: 600,
              pointerEvents: "none",
              zIndex: 7,
            }}
          >
            Hoje
          </div>
        </>
      )}

      {nowPx != null && (
        <>
          <div
            style={{
              position: "absolute",
              left: nowPx,
              top: plotBox.top,
              height: plotBox.plotH,
              borderLeft: "2.5px dashed rgba(228,14,14,0.62)",
              pointerEvents: "none",
              zIndex: 8,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: nowPx + 6,
              top: plotBox.top + LABEL_TOP_PAD,
              color: "rgba(228,14,14,0.92)",
              fontSize: 12,
              fontWeight: 600,
              pointerEvents: "none",
              zIndex: 9,
            }}
          >
            Agora
          </div>
        </>
      )}

      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={MARGIN}>
          <CartesianGrid stroke={grid} strokeDasharray="4 6" />

          <XAxis
            dataKey="__x"
            type="number"
            domain={xDomain}
            tickFormatter={tickFmtDate}
            tick={{ fill: tick, fontSize: 12 }}
            axisLine={{ stroke: axis }}
            tickLine={{ stroke: axis }}
            minTickGap={14}
          />

          <YAxis
            yAxisId="left"
            width={Y_AXIS_W}
            tick={{ fill: tick, fontSize: 12 }}
            axisLine={{ stroke: axis }}
            tickLine={{ stroke: axis }}
            tickFormatter={fmtCompact}
          />

          <Tooltip
            labelFormatter={labelFmtDate}
            formatter={tooltipFormatter}
            contentStyle={{
              background: "rgba(10,15,25,0.92)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12,
              color: t?.text ?? "white",
            }}
            labelStyle={{ color: t?.muted ?? "rgba(255,255,255,0.7)" }}
          />

          {series.map((s) => {
            const stroke = s.stroke ?? t?.chartPrimary ?? "rgba(0,200,200,0.95)";
            const dashed = !!s.dashed;
            const width = Number(s.width ?? 2);
            const connectNulls = s.connectNulls === true;

            return (
              <Line
                key={s.key}
                yAxisId="left"
                type="monotone"
                dataKey={`__${s.key}`}
                name={s.name ?? s.key}
                stroke={stroke}
                strokeWidth={width}
                strokeDasharray={dashed ? "6 6" : undefined}
                dot={false}
                activeDot={{ r: 4, stroke, strokeWidth: 2, fill: "rgba(0,0,0,0.85)" }}
                isAnimationActive={false}
                connectNulls={connectNulls}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
